/**
 * Kitchen prints — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_wall_art`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const kitchenPrintsPack: ToilePack = {
    slug: 'kitchen-prints',
    category: 'wall-art',
    name: 'Kitchen prints',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'lemons', label: 'Lemon branch', subject: 'a lemon branch carrying two ripe lemons, one cut half showing its segments, and three glossy leaves'},
        {key: 'herbs', label: 'Herb row', subject: 'five herb sprigs standing side by side at even spacing, basil, rosemary, thyme, sage and mint, each drawn unnamed'},
        {key: 'pasta', label: 'Pasta chart', subject: 'nine pasta shapes set out in a three-by-three grid, farfalle, fusilli, penne and rigatoni among them, each drawn alone and unnamed'},
    ],
    styles: [
        {key: 'wash', label: 'Watercolor', style: 'a loose botanical watercolor with soft bleeding edges, visible paper grain and no outlines', palette: 'Warm lemon yellow, olive green and tomato red on warm white paper', framing: 'Centered on the sheet with a generous white margin all round'},
        {key: 'cutpaper', label: 'Cut paper', style: 'flat cut-paper shapes with no outlines, hard edges and smooth gradient-free fills', palette: 'A limited palette of five colors: terracotta, mustard, sage, cream and charcoal', framing: 'Full-bleed across the sheet with the shapes laid on one solid color'},
        {key: 'lino', label: 'Linocut', style: 'a two-color linocut with visible gouge marks, slight ink misregistration and a rough printed grain', palette: 'Two-color ink, charcoal black and warm terracotta on off-white paper', framing: 'Centered inside a thin hand-cut border with an even margin'},
    ],
    directions: ['in blue and white', 'for a coffee corner', 'with a wine bottle instead'],
    seo: {
        title: 'Kitchen wall art: lemon branch, herbs, pasta chart',
        metaDescription: 'Nine kitchen prints — lemon branch, herb row and a pasta chart — in watercolor, cut paper or linocut style, warm palette for beside the stove.',
        h1: 'Kitchen wall art: lemon branch, herb row and pasta chart',
        tagline: 'Nine prints, three kitchen subjects, three finishes each — warm palette, clean at eye level over the counter.',
        intro: 'The print beside the stove: a lemon branch in watercolor, a row of herbs with their names, a chart of pasta shapes. These nine cross those three subjects with a soft watercolor wash, a cut-paper collage look and a textured linocut finish — a warm palette, the art that survives steam and looks good at eye level over the counter.',
        useCases: [
            'the wall beside the stove',
            'a coffee or breakfast corner',
            'a set of two or three for a kitchen run',
            'a gift for someone who cooks',
        ],
        faq: [
            {q: 'What size do these print at?', a: 'Each print is drawn at A3 proportions with an even margin, so it prints clean at A3 or scales to A4.'},
            {q: 'Are the herb or pasta names spelled correctly?', a: 'Yes — small labels are set exactly as composed, in the classic kitchen-chart register.'},
            {q: 'Can I ask for a different subject, like a wine bottle?', a: 'Yes — the direction field takes that request and redraws it in the same three finishes.'},
        ],
    },
}
